import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import YMMTSelector from "../components/storefront/YMMTSelector";
import VehicleSearch from "../components/storefront/VehicleSearch";
import ProductCard from "../components/storefront/ProductCard";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { BUSINESS, POSTAL_ADDRESS_LD } from "../lib/business";
import { ArrowRight, Shield, Truck, Wrench, Award, ShieldCheck, Headphones } from "lucide-react";

const HERO_WEBP = "/assets/hero.webp";
const HERO_JPG = "/assets/hero.jpg";
const MIRROR_IMG = "https://images.pexels.com/photos/1729997/pexels-photo-1729997.jpeg";
const WS_IMG = "https://images.pexels.com/photos/29755711/pexels-photo-29755711.jpeg";
const FERRARI_IMG = "https://images.pexels.com/photos/10199222/pexels-photo-10199222.jpeg";

const EXOTIC_BRANDS = ["ferrari", "lamborghini", "bentley", "mclaren", "aston-martin", "rolls-royce", "porsche"];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products?limit=8&sort=newest").then(({ data }) => setFeatured(data.products));
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: "Lecis Auto Glass — Side Mirrors & Windshields for Exotic Vehicles",
    description: "OEM-style and aftermarket side mirrors and windshields for Ferrari, Lamborghini, Bentley, McLaren, Porsche, Maserati & more. Fitment-verified, shipped nationwide from California.",
    canonicalPath: "/",
    jsonLd: {
      organization: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Lecis Auto Glass",
        url: origin || "https://lecisautoglass.com",
        logo: `${origin}/logo.png`,
        email: BUSINESS.email,
        telephone: BUSINESS.phoneHref,
        address: POSTAL_ADDRESS_LD,
        contactPoint: [{ "@type": "ContactPoint", telephone: BUSINESS.phoneHref, email: BUSINESS.email, contactType: "Customer Service", areaServed: "US", availableLanguage: "en" }],
      },
      website: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Lecis Auto Glass",
        url: origin || "https://lecisautoglass.com",
        potentialAction: {
          "@type": "SearchAction",
          target: `${origin}/shop?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      localBusiness: {
        "@context": "https://schema.org",
        "@type": "AutoPartsStore",
        "@id": `${origin}/#localbusiness`,
        name: "Lecis Auto Glass",
        url: origin || "https://lecisautoglass.com",
        image: `${origin}/logo.png`,
        telephone: BUSINESS.phoneHref,
        email: BUSINESS.email,
        address: POSTAL_ADDRESS_LD,
        areaServed: { "@type": "Country", name: "United States" },
        openingHoursSpecification: [{
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "08:00",
          closes: "17:00",
        }],
        priceRange: "$$$$",
      },
    },
  });

  return (
    <StorefrontLayout>
      {/* Hero */}
      <section className="relative min-h-[88vh] md:min-h-screen flex items-center overflow-hidden noise" data-testid="home-hero">
        <div className="absolute inset-0">
          <picture>
            <source srcSet={HERO_WEBP} type="image/webp" />
            <img src={HERO_JPG} alt="" className="w-full h-full object-cover" fetchpriority="high" decoding="async" />
          </picture>
          <div className="absolute inset-0 hero-gradient" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 fade-up">
              <div className="text-[10px] uppercase tracking-[0.4em] text-red-500 font-bold mb-6">
                Fair Oaks, California · Shipping nationwide
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-bold mb-6">
                Glass that<br/>
                <span className="text-zinc-500">belongs on</span><br/>
                a Ferrari.
              </h1>
              <p className="text-zinc-400 text-base md:text-lg max-w-md leading-relaxed mb-8">
                Side mirrors and windshields for Ferrari, Lamborghini, Bentley, McLaren, Aston Martin, Rolls-Royce, Porsche — and select US brand side mirrors. OEM-style and aftermarket, fitment-verified.
              </p>
              <div className="mb-8 max-w-lg relative z-20" data-testid="hero-vehicle-search">
                <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-2">Search your vehicle</div>
                <VehicleSearch />
                <p className="text-[11px] text-zinc-500 mt-2">Try “Ferrari 488 GTB”, “Lamborghini Huracán”, “Porsche 911” or “Ford F650”.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/shop" data-testid="hero-shop-btn" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 h-12 text-sm tracking-wide font-medium transition rounded-sm">
                  Shop all parts <ArrowRight size={16} />
                </Link>
                <Link to="/brands" data-testid="hero-brands-btn" className="inline-flex items-center gap-2 border border-white/20 hover:border-white/50 text-white px-6 h-12 text-sm tracking-wide transition rounded-sm">
                  Browse by brand
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 fade-up fade-up-delay-2">
              <YMMTSelector variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-white/5 bg-white/[0.015]" data-testid="home-trust-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
          {[
            { icon: Award, title: "15+ Years Experience", sub: "Auto glass industry expertise" },
            { icon: Truck, title: "Nationwide Shipping", sub: "Insured & tracked delivery" },
            { icon: ShieldCheck, title: "Secure Checkout", sub: "PCI-compliant via Stripe" },
            { icon: Headphones, title: "Expert Support", sub: BUSINESS.hoursShort },
          ].map((t) => (
            <div key={t.title} className="flex items-center gap-3" data-testid={`trust-bar-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <t.icon size={22} className="text-red-500 shrink-0" />
              <div>
                <div className="text-sm font-medium text-white leading-tight">{t.title}</div>
                <div className="text-[11px] text-zinc-500 leading-tight">{t.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32" data-testid="home-categories">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-3">Categories</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-medium">Premium Products. Done Right.</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/shop/side-mirror" data-testid="cat-side-mirror" className="group relative aspect-[4/3] overflow-hidden bg-zinc-900 rounded-md">
            <img src={MIRROR_IMG} alt="Side mirrors" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">Category</div>
              <div className="font-display text-3xl md:text-4xl tracking-tight font-medium mb-2">Side Mirrors</div>
              <div className="text-sm text-zinc-300">Left · Right · Pair · Heated · Camera</div>
            </div>
          </Link>
          <Link to="/shop/windshield" data-testid="cat-windshield" className="group relative aspect-[4/3] overflow-hidden bg-zinc-900 rounded-md">
            <img src={WS_IMG} alt="Windshields" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">Category</div>
              <div className="font-display text-3xl md:text-4xl tracking-tight font-medium mb-2">Windshields</div>
              <div className="text-sm text-zinc-300">Laminated · Acoustic · UV-rejecting</div>
            </div>
          </Link>
        </div>
      </section>

      {/* Exotic brands strip */}
      <section className="border-y border-white/5 py-16" data-testid="home-brands-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-2">Specialty</div>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight">Exotic brand coverage</h3>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {EXOTIC_BRANDS.map((slug) => (
              <Link key={slug} to={`/${slug}`} data-testid={`brand-link-${slug}`} className="font-display text-xl md:text-2xl text-zinc-500 hover:text-white transition tracking-tight uppercase">
                {slug.replace("-", " ")}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32" data-testid="home-featured">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-3">In stock now</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-medium">Recently added</h2>
          </div>
          <Link to="/shop" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-2">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="home-trust">
        <div className="border border-white/5 p-8 rounded-md">
          <Shield size={22} className="text-red-500 mb-4" />
          <div className="font-display text-lg mb-2">Fitment-verified</div>
          <div className="text-sm text-zinc-400 leading-relaxed">Every product is tied to a normalized Y/M/M/T record so you order with confidence.</div>
        </div>
        <div className="border border-white/5 p-8 rounded-md">
          <Truck size={22} className="text-red-500 mb-4" />
          <div className="font-display text-lg mb-2">Nationwide shipping</div>
          <div className="text-sm text-zinc-400 leading-relaxed">Insured, tracked delivery from Fair Oaks, California to your door anywhere in the US.</div>
        </div>
        <div className="border border-white/5 p-8 rounded-md">
          <Wrench size={22} className="text-red-500 mb-4" />
          <div className="font-display text-lg mb-2">OEM-style & aftermarket</div>
          <div className="text-sm text-zinc-400 leading-relaxed">Curated options to match how you drive — concours-grade restoration or daily.</div>
        </div>
      </section>
    </StorefrontLayout>
  );
}
